import { z } from 'zod'

import { base } from '@/lib/orpc/errors'
import {
  addressSchema,
  chainIdSchema,
  cursorSchema,
  pageOutput,
  uint256StringSchema,
  vaultV2ApyBreakdownSchema
} from '@/lib/orpc/schemas'

/** The bridge's own bound on every list filter. */
const MAX_FILTER_VALUES = 100

/** A `<chain_id>:<address>` selector, as the `assets` and collateral facets return them. */
const assetSelectorSchema = z.string().min(1).max(64)

const selectors = z.array(assetSelectorSchema).max(MAX_FILTER_VALUES)
const usdBound = z.number().nonnegative()

export const VAULT_LIST_ORDER_BY = [
  'totalAssetsUsd',
  'name',
  'netApy',
  'netApyExcludingRewards',
  'performanceFee',
  'managementFee',
  'liquidityUsd',
  'creationTimestamp'
] as const

export const vaultListInputSchema = z.strictObject({
  /** Page size; the bridge's default (100) when omitted. */
  limit: z.number().int().min(1).max(1000).optional(),
  /** The `nextCursor` of the previous page, called with the same filters. */
  cursor: cursorSchema.optional(),
  orderBy: z.enum(VAULT_LIST_ORDER_BY).optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  /** Substring of the vault name or address, the asset symbol or address, or a curator name. */
  search: z.string().min(1).max(100).optional(),
  /** Limited to the chains the app serves; all of them when omitted. */
  chainIds: z.array(chainIdSchema).max(MAX_FILTER_VALUES).optional(),
  versions: z
    .array(z.enum(['1.0', '1.1', '2.0']))
    .max(3)
    .optional(),
  assets: selectors.optional(),
  curatorIds: z.array(z.string().min(1).max(100)).max(MAX_FILTER_VALUES).optional(),
  /** Keeps the vaults allocating to any of these collaterals. */
  collateralExposure: selectors.optional(),
  /** Drops the vaults allocating to any of these collaterals. */
  collateralExclusion: selectors.optional(),
  totalAssetsUsdGte: usdBound.optional(),
  totalAssetsUsdLte: usdBound.optional(),
  liquidityUsdGte: usdBound.optional(),
  liquidityUsdLte: usdBound.optional(),
  /** Bounds on the net APY (native net of fees plus rewards), as decimal rates. */
  netApyGte: z.number().optional(),
  netApyLte: z.number().optional(),
  /** Also return `facets`; `facets` is null otherwise. */
  includeFacets: z.boolean().optional()
})

export type VaultListInput = z.infer<typeof vaultListInputSchema>

const tokenSchema = z.strictObject({
  address: addressSchema,
  symbol: z.string(),
  decimals: z.number().int().nonnegative(),
  logoUri: z.string().nullable()
})

const vaultListItemSchema = z.strictObject({
  chainId: chainIdSchema,
  address: addressSchema,
  /** `1.0`, `1.1` or `2.0`, read as a plain string. */
  version: z.string(),
  name: z.string(),
  creationTimestamp: z.number().int().nonnegative(),
  asset: z.strictObject({
    address: addressSchema,
    symbol: z.string(),
    name: z.string().nullable(),
    decimals: z.number().int().nonnegative(),
    logoUri: z.string().nullable(),
    priceUsd: z.number().nullable(),
    /** The asset's own native APR (staking, lending), null when it has none or it can't be read. */
    yield: z.number().nullable()
  }),
  totalAssets: uint256StringSchema,
  /** Null when the asset has no USD price, never 0. Same for every `*Usd` field. */
  totalAssetsUsd: z.number().nullable(),
  liquidity: uint256StringSchema,
  liquidityUsd: z.number().nullable(),
  performanceFee: z.number(),
  /** Null for V1 vaults. */
  managementFee: z.number().nullable(),
  curators: z.array(
    z.strictObject({
      id: z.string(),
      name: z.string(),
      image: z.string().nullable(),
      aumUsd: z.number()
    })
  ),
  warnings: z.array(
    z.strictObject({
      type: z.string(),
      level: z.string(),
      content: z.string().nullable(),
      delisted: z.boolean()
    })
  ),
  /** The vault-level reward set, allocation-level market rewards already included. */
  rewards: z.array(
    z.strictObject({
      asset: tokenSchema.extend({ chainId: chainIdSchema }),
      /** Rewards-only APR. */
      supplyApr: z.number()
    })
  ),
  exposure: z.array(
    z.strictObject({
      /** Null for idle assets. */
      collateralAsset: tokenSchema.nullable(),
      exposureUsd: z.number().nullable()
    })
  ),
  /** Gross native APY before fees. Null for V1 vaults. */
  apy: z.number().nullable(),
  /** Native APY net of fees, excluding rewards. */
  netApyExcludingRewards: z.number().nullable(),
  /** Native APY net of fees plus the rewards-only APRs. */
  netApy: z.number().nullable(),
  /** The V2 rate cap on the realised APY. Null for V1 vaults. */
  maxRate: z.number().nullable(),
  /** The organic / surplus split of the spot APY. Null for V1 vaults. */
  apyBreakdown: vaultV2ApyBreakdownSchema
})

export type VaultListItem = z.infer<typeof vaultListItemSchema>

const facetTokenSchema = z
  .strictObject({
    chainId: chainIdSchema,
    address: addressSchema,
    symbol: z.string(),
    logoUri: z.string().nullable(),
    tags: z.array(z.string()).nullable()
  })
  .nullable()

const count = z.number().int().nonnegative()
const tokenFacetSchema = z.array(
  z.strictObject({ value: z.string(), count, metadata: facetTokenSchema })
)

/**
 * Per-value counts of the vaults matching every other filter. Each `value` is what the
 * matching filter accepts back.
 */
const vaultListFacetsSchema = z.strictObject({
  chainIds: z.array(z.strictObject({ value: chainIdSchema, count, totalAssetsUsd: z.number() })),
  versions: z.array(z.strictObject({ value: z.string(), count })),
  assets: tokenFacetSchema,
  curatorIds: z.array(
    z.strictObject({
      value: z.string(),
      count,
      metadata: z.strictObject({ name: z.string(), image: z.string().nullable() }).nullable()
    })
  ),
  collateralExposure: tokenFacetSchema,
  /**
   * The collateral options for the exclusion filter: the collateral exposure facet computed
   * without `collateralExclusion`, so an excluded collateral stays listed.
   */
  collateralExclusion: tokenFacetSchema
})

export type VaultListFacets = z.infer<typeof vaultListFacetsSchema>

export const vaultListOutputSchema = pageOutput(vaultListItemSchema).extend({
  /** Vaults matching the filters, across every page. */
  countTotal: count,
  facets: vaultListFacetsSchema.nullable()
})

export type VaultListOutput = z.infer<typeof vaultListOutputSchema>

/** One page of listed V1 and V2 vaults, filtered and sorted by the bridge. */
export const vaultList = base.input(vaultListInputSchema).output(vaultListOutputSchema)
